<template>
  <div data-slot="leadgen-form-leads-list" class="flex flex-col gap-3">
    <div class="flex flex-wrap items-center gap-3">
      <div class="relative min-w-0 flex-1 md:basis-0">
        <search-icon class="pointer-events-none absolute top-1/2 left-3 z-10 size-4 -translate-y-1/2 text-muted-foreground" />
        <ui-input
          v-model="searchTerm"
          type="search"
          class="pl-9"
          :placeholder="tr('Search') + '...'"
          :aria-label="tr('Search')"
        />
      </div>

      <ui-responsive-filters
        :active-count="activeFilterCount"
        :title="tr('Filters')"
        :clear-label="tr('Clear filters')"
        :close-label="tr('Close')"
        @clear="() => clearSelectFilters()"
      >
        <template #default="{ stacked }">
          <leadgen-form-leads-filters :stacked="stacked" :filters="filters" @change="(name, value) => setFilter(name, value)" />
        </template>
      </ui-responsive-filters>

      <ui-dropdown-menu>
        <ui-dropdown-menu-trigger as-child>
          <ui-button variant="outline" :disabled="!leadgenForm" :aria-label="tr('Columns')">
            <columns-3-icon />
            <span class="max-md:hidden">{{ tr('Columns') }}</span>
          </ui-button>
        </ui-dropdown-menu-trigger>
        <ui-dropdown-menu-content align="end" class="max-h-80 w-64 overflow-y-auto">
          <ui-dropdown-menu-checkbox-item
            v-for="column in availableColumns"
            :key="column.key"
            :model-value="visibleColumnKeys.includes(column.key)"
            @update:model-value="() => toggleColumn(column.key)"
            @select="(event) => event.preventDefault()"
          >
            {{ column.label }}
          </ui-dropdown-menu-checkbox-item>
        </ui-dropdown-menu-content>
      </ui-dropdown-menu>
    </div>

    <empty-screen
      v-if="leadgenFormId && !isLoading && !isRefreshing && isListEmpty"
      icon="sad"
      :title="hasFilters ? tr('No leads match the filters') : tr('No leads yet')"
      :description="hasFilters
        ? tr('Try another search term or clear some of the filters.')
        : tr('When users submit the lead generation form through your ads, their information will appear here. You can track all form submissions and export the data for further processing.')"
    >
      <template v-if="hasFilters" #under-description>
        <ui-button variant="outline" class="self-start" @click="() => clearFilters()">
          {{ tr('Clear filters') }}
        </ui-button>
      </template>
    </empty-screen>

    <template v-else>
      <!-- Stacked cards on phones. -->
      <div class="flex flex-col gap-3 md:hidden">
        <template v-if="!isRefreshing">
          <router-link
            v-for="lead in leads"
            :key="'lead-card--' + lead.id"
            :to="leadLink(lead)"
            class="glass-tile flex flex-col gap-3 rounded-xl p-4 text-sm text-foreground no-underline"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="flex min-w-0 flex-col gap-0.5">
                <span class="truncate font-semibold">{{ lead.displayName || tr('Lead') }}</span>
                <span class="text-xs text-muted-foreground tabular-nums">{{ formatDate(lead.created_at) }}</span>
              </div>
              <lead-status-select
                :model-value="lead.status"
                :disabled="!canManage"
                :loading="savingLeadIds.includes(lead.id)"
                @update:model-value="(status) => changeStatus(lead, status)"
              />
            </div>
            <div class="flex items-end justify-between gap-3">
              <dl v-if="visibleFieldColumns.length !== 0" class="m-0 flex min-w-0 flex-1 flex-col gap-2">
                <div v-for="column in visibleFieldColumns" :key="column.key" class="flex flex-col gap-0.5">
                  <dt class="text-xs font-semibold text-muted-foreground">{{ column.label }}</dt>
                  <dd class="m-0 [overflow-wrap:anywhere]">
                    <lead-field-value :lead="lead" :field="column.field" />
                  </dd>
                </div>
              </dl>
              <!-- The look of ArrowLink; the card itself is the link. -->
              <span class="glass-button ml-auto inline-flex size-8 shrink-0 items-center justify-center rounded-full text-primary" aria-hidden="true">
                <chevron-right-icon class="size-4" />
              </span>
            </div>
          </router-link>
        </template>

        <template v-if="hasMore || isLoading || isRefreshing">
          <div
            v-for="i in 3"
            :key="'skeleton-card--' + i"
            class="glass-tile flex flex-col gap-2 rounded-xl p-4"
          >
            <ui-skeleton class="h-4 w-36" />
            <ui-skeleton class="h-3.5 w-24" />
            <ui-skeleton class="h-4 w-48" />
          </div>
        </template>
      </div>

      <div class="glass-panel rounded-xl px-2 py-1 max-md:hidden">
        <ui-table>
          <ui-table-header>
            <ui-table-row class="hover:bg-transparent [&_th]:text-xs [&_th]:font-semibold [&_th]:text-muted-foreground">
              <ui-table-head class="min-w-44">{{ tr('Name') }}</ui-table-head>
              <ui-table-head
                v-for="column in visibleColumns"
                :key="column.key"
                :class="column.headClass"
              >
                {{ column.label }}
              </ui-table-head>
            </ui-table-row>
          </ui-table-header>
          <ui-table-body>
            <template v-if="!isRefreshing">
              <ui-table-row
                v-for="lead in leads"
                :key="'lead--' + lead.id"
                class="cursor-pointer"
                @click="() => openLead(lead)"
              >
                <ui-table-cell class="font-semibold">
                  <router-link :to="leadLink(lead)" class="text-foreground no-underline hover:underline" @click.stop>
                    {{ lead.displayName || tr('Lead') }}
                  </router-link>
                </ui-table-cell>
                <ui-table-cell
                  v-for="column in visibleColumns"
                  :key="column.key"
                  :class="column.cellClass"
                >
                  <lead-status-select
                    v-if="column.key === 'status'"
                    :model-value="lead.status"
                    :disabled="!canManage"
                    :loading="savingLeadIds.includes(lead.id)"
                    @update:model-value="(status) => changeStatus(lead, status)"
                  />
                  <lead-assignee-select
                    v-else-if="column.key === 'assignee'"
                    :assignee="lead.assignee"
                    :members="companyMemberOptions"
                    :disabled="!canManage || userListIsLoading"
                    :loading="savingLeadIds.includes(lead.id)"
                    @update:assignee="(userId) => changeAssignee(lead, userId)"
                  />
                  <template v-else-if="column.key === 'campaign'">
                    <span v-if="lead.campaign" class="inline-flex max-w-56 truncate rounded-md bg-foreground/8 px-2 py-0.5 text-xs font-medium text-foreground/80">
                      {{ lead.campaign.name }}
                    </span>
                    <span v-else class="text-muted-foreground">–</span>
                  </template>
                  <template v-else-if="column.key === 'created_at'">
                    {{ formatToYMD(lead.created_at) }}
                    <span class="block text-xs text-muted-foreground">{{ formatTime(lead.created_at) }}</span>
                  </template>
                  <lead-field-value v-else :lead="lead" :field="column.field" />
                </ui-table-cell>
              </ui-table-row>
            </template>

            <template v-if="hasMore || isLoading || isRefreshing">
              <ui-table-row
                v-for="i in 3"
                :key="'skeleton--' + i"
                class="hover:bg-transparent"
              >
                <ui-table-cell><ui-skeleton class="h-4 w-32" /></ui-table-cell>
                <ui-table-cell v-for="column in visibleColumns" :key="column.key">
                  <ui-skeleton class="h-4 w-24" />
                </ui-table-cell>
              </ui-table-row>
            </template>
          </ui-table-body>
        </ui-table>
      </div>

      <!-- One trigger for both layouts: the hidden one would always count as in view. -->
      <div
        v-if="hasMore && !isRefreshing"
        ref="scrollPagerTrigger"
        class="h-px"
        aria-hidden="true"
      />
    </template>
  </div>
</template>

<script>
import {
  ChevronRight,
  Columns3,
  Search,
} from '@lucide/vue';

import { findNameFields } from '@/entities/LeadgenFormLead.js';
import CompanyMemberListMixin from '@/mixins/CompanyMemberList.js';
import ScrollPagerMixin from '@/mixins/ScrollPager.js';
import LeadgenFormService from '@/services/LeadgenForm.js';
import LeadgenFormLeadService from '@/services/LeadgenFormLead.js';
import EmptyScreen from '@/ui/components/EmptyScreen.vue';
import LeadAssigneeSelect from '@/ui/components/LeadAssigneeSelect.vue';
import LeadFieldValue from '@/ui/components/LeadFieldValue.vue';
import LeadStatusSelect from '@/ui/components/LeadStatusSelect.vue';
import LeadgenFormLeadsFilters from '@/ui/components/LeadgenFormLeadsFilters.vue';
import { LEAD_STATUSES, leadStatusOptions } from '@/ui/utils/leadStatus.js';
import { formatTime, formatToYMD } from '@/utils/date.js';
import debounce from '@/utils/debounce.js';

const ITEM_PER_PAGE = 20;

// The kit select works with non-empty strings.
const ALL = 'all';
const UNASSIGNED = 'unassigned';

const FIELD_PREFIX = 'field:';

// Loads the next page a bit before the end of the list is reached.
const SCROLL_MARGIN = 300;

export default {
  name: 'LeadgenFormLeadsList',
  components: {
    ChevronRightIcon: ChevronRight,
    Columns3Icon: Columns3,
    EmptyScreen,
    LeadAssigneeSelect,
    LeadFieldValue,
    LeadgenFormLeadsFilters,
    LeadStatusSelect,
    SearchIcon: Search,
  },
  mixins: [ CompanyMemberListMixin, ScrollPagerMixin ],
  props: {
    leadgenFormId: {
      type: String,
      required: false,
      default: null,
    },
    // A fixed campaign filter (the leads of an automation).
    campaignIds: {
      type: Array,
      default: null,
    },
    // The path the lead ids are appended to for the lead's own route.
    leadRouteBase: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      ALL,
      UNASSIGNED,
      isLoading: false,
      isRefreshing: false,
      isDownloading: false,
      hasMore: true,
      page: 0,
      leads: [],
      leadgenForm: null,
      savingLeadIds: [],
      searchTerm: typeof this.$route.query.q === 'string' ? this.$route.query.q : '',
      debouncedApplySearch: debounce(() => this.applySearch(), 500),
    };
  },
  computed: {
    loggedInUserId() {
      return this.$root.loggedInUser?.id ?? null;
    },
    canManage() {
      return this.$root.loggedInUser?.level !== 'restricted_viewer';
    },
    statusOptions() {
      return leadStatusOptions(this.tr);
    },
    otherMemberOptions() {
      return this.companyMemberOptions.filter(option => option.id !== this.loggedInUserId);
    },
    campaignOptions() {
      return this.leadgenForm?.lead_campaigns ?? [];
    },
    statusFilter() {
      const value = this.$route.query.status;

      return LEAD_STATUSES.includes(value) ? value : ALL;
    },
    assigneeFilter() {
      const value = this.$route.query.assignee;

      return typeof value === 'string' && value ? value : ALL;
    },
    campaignFilter() {
      const value = this.$route.query.campaign;

      return typeof value === 'string' && value ? value : ALL;
    },
    appliedSearch() {
      const value = this.$route.query.q;

      return typeof value === 'string' ? value.trim() : '';
    },
    filters() {
      const filters = [
        {
          name: 'status',
          label: this.tr('Status'),
          value: this.statusFilter,
          triggerClass: 'md:max-w-44',
          options: [ { value: ALL, label: this.tr('All statuses') }, ...this.statusOptions ],
        },
        {
          name: 'assignee',
          label: this.tr('Assignee'),
          value: this.assigneeFilter,
          triggerClass: 'md:max-w-52',
          options: [
            { value: ALL, label: this.tr('All assignees') },
            ...(this.loggedInUserId ? [ { value: this.loggedInUserId, label: this.tr('My leads') } ] : []),
            { value: UNASSIGNED, label: this.tr('Unassigned') },
            ...(this.otherMemberOptions.length !== 0 ? [ { value: 'members', separator: true } ] : []),
            ...this.otherMemberOptions.map(option => ({ value: option.id, label: option.fullname })),
          ],
        },
      ];

      if (!this.campaignIds && this.campaignOptions.length !== 0) {
        filters.push({
          name: 'campaign',
          label: this.tr('Campaign'),
          value: this.campaignFilter,
          triggerClass: 'md:max-w-56',
          options: [
            { value: ALL, label: this.tr('All campaigns') },
            ...this.campaignOptions.map(campaign => ({ value: campaign.id, label: campaign.name })),
          ],
        });
      }

      return filters;
    },
    // The selects set (the search is shown anyway).
    activeFilterCount() {
      return this.filters.filter(filter => filter.value !== ALL).length;
    },
    hasFilters() {
      return this.statusFilter !== ALL
        || this.assigneeFilter !== ALL
        || this.campaignFilter !== ALL
        || this.appliedSearch !== '';
    },
    // The filters together, so one watcher reloads the list on any change.
    filterKey() {
      return [ this.statusFilter, this.assigneeFilter, this.campaignFilter, this.appliedSearch ].join('|');
    },
    availableColumns() {
      const fieldColumns = (this.leadgenForm?.lead_fields ?? []).map(field => ({
        key: FIELD_PREFIX + field,
        field,
        label: this.formatFieldName(field),
        headClass: 'min-w-36',
        cellClass: 'whitespace-normal',
      }));

      return [
        { key: 'status', label: this.tr('Status'), headClass: 'w-36' },
        { key: 'assignee', label: this.tr('Assignee'), headClass: 'w-52' },
        ...fieldColumns,
        { key: 'campaign', label: this.tr('Campaign'), headClass: 'min-w-40' },
        { key: 'created_at', label: this.tr('Created'), headClass: 'w-32', cellClass: 'tabular-nums' },
      ];
    },
    defaultColumnKeys() {
      const keys = [ 'status', 'assignee' ];

      // The name is in the first column already.
      const fields = this.leadgenForm?.lead_fields ?? [];
      const nameFields = findNameFields(fields);
      fields
        .filter(field => !nameFields.includes(field))
        .forEach(field => keys.push(FIELD_PREFIX + field));

      // Not for the leads of a single campaign.
      if (!this.campaignIds && this.campaignOptions.length !== 0) {
        keys.push('campaign');
      }
      keys.push('created_at');

      return keys;
    },
    visibleColumnKeys() {
      return this.leadgenForm?.columns ?? this.defaultColumnKeys;
    },
    visibleColumns() {
      return this.availableColumns.filter(column => this.visibleColumnKeys.includes(column.key));
    },
    visibleFieldColumns() {
      return this.visibleColumns.filter(column => column.field);
    },
    isListEmpty() {
      return this.leads.length === 0;
    },
  },
  watch: {
    leadgenFormId: {
      immediate: true,
      handler(newVal) {
        if (!newVal) {
          this.reset();
          this.leadgenForm = null;
          return;
        }

        this.resetAndLoad()
          .then(() => this.loadLeadgenForm()) // Get updated leads count and fetched time.
          .catch(err => this.error(err));
      },
    },
    filterKey() {
      this.resetAndLoad();
    },
    // The search from the address bar (back button, shared link).
    appliedSearch(value) {
      if (value !== this.searchTerm.trim()) {
        this.searchTerm = value;
      }
    },
    searchTerm(value) {
      if (value.trim() === '') {
        this.debouncedApplySearch.cancel();
        this.applySearch();
      }
      else {
        this.debouncedApplySearch();
      }
    },
    isRefreshing(newVal) {
      if (newVal) {
        this.reset();
        return;
      }

      this.resetAndLoad();
    },
  },
  beforeUnmount() {
    this.debouncedApplySearch.cancel();
  },
  methods: {
    formatToYMD,
    formatTime,
    formatDate(date) {
      return formatToYMD(date) + ' ' + formatTime(date);
    },
    formatFieldName(fieldName) {
      // Convert "car_make" to "Car Make"
      return fieldName
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    leadLink(lead) {
      return { path: this.leadRouteBase + '/' + lead.id, query: this.$route.query };
    },
    openLead(lead) {
      this.$router.push(this.leadLink(lead));
    },
    // The filters live in the query string, so a filtered list can be shared and comes back
    // after the lead modal.
    setFilter(name, value) {
      const query = { ...this.$route.query };
      if (!value || value === ALL) {
        delete query[name];
      }
      else {
        query[name] = value;
      }

      this.$router.replace({ query });
    },
    applySearch() {
      const value = this.searchTerm.trim();
      if (value !== this.appliedSearch) {
        this.setFilter('q', value);
      }
    },
    clearSelectFilters() {
      const query = { ...this.$route.query };
      this.filters.forEach(filter => delete query[filter.name]);

      this.$router.replace({ query });
    },
    clearFilters() {
      this.searchTerm = '';
      this.$router.replace({ query: {} });
    },
    loadLeadgenForm() {
      return LeadgenFormService.getById(this.leadgenFormId, { campaignIds: this.campaignIds })
        .then(form => this.leadgenForm = form)
        .catch(err => this.error(err));
    },
    reset() {
      this.leads = [];
      this.page = 0;
      this.hasMore = true;
    },
    resetAndLoad() {
      this.reset();
      this.isLoading = false;
      return this.loadNewData();
    },
    loadNewData() {
      if (this.isLoading || !this.hasMore) {
        return Promise.resolve();
      }

      this.isLoading = true;
      ++this.page;

      const params = {
        page: this.page,
        limit: ITEM_PER_PAGE,
        search: this.appliedSearch,
      };

      if (this.campaignIds && this.campaignIds.length > 0) {
        params.campaignIds = this.campaignIds;
      }
      else if (this.campaignFilter !== ALL) {
        params.campaignIds = [ this.campaignFilter ];
      }
      if (this.statusFilter !== ALL) {
        params.statuses = [ this.statusFilter ];
      }
      if (this.assigneeFilter !== ALL) {
        params.assigneeIds = [ this.assigneeFilter ];
      }

      // A newer filter supersedes the request still running.
      const requestKey = this.filterKey;

      return LeadgenFormService.getLeads(this.leadgenFormId, params)
        .then(resp => {
          if (requestKey !== this.filterKey) {
            return resp;
          }

          this.leads.push(...resp);
          this.hasMore = resp.length === ITEM_PER_PAGE;

          return resp;
        })
        .catch(err => {
          this.hasMore = false;
          this.error(err);
        })
        .finally(() => {
          if (requestKey !== this.filterKey) {
            return;
          }

          this.isLoading = false;
          if (this.hasMore) {
            this.$nextTick(() => this.watchScrollEnd());
          }
        });
    },
    watchScrollEnd() {
      const trigger = this.$refs.scrollPagerTrigger;
      if (!trigger) {
        return;
      }

      // The pager only observes a trigger out of view, so a short page loads on right away.
      if (trigger.getBoundingClientRect().top <= window.innerHeight + SCROLL_MARGIN) {
        this.loadNewData();
        return;
      }

      this.initScrollPager('scrollPagerTrigger', 'loadNewData', { rootMargin: '0px 0px ' + SCROLL_MARGIN + 'px 0px' });
    },
    // A lead changed in its modal.
    updateLead(updatedLead) {
      const lead = this.leads.find(item => item.id === updatedLead.id);
      if (lead) {
        lead.updateWith({ status: updatedLead.status, assignee: updatedLead.assignee });
      }
    },
    saveLead(lead, request) {
      if (this.savingLeadIds.includes(lead.id)) {
        return;
      }

      this.savingLeadIds.push(lead.id);

      request()
        .then(updatedLead => {
          this.updateLead(updatedLead);
          this.$toast.clear();
          this.$toast.success(this.tr('Lead updated.'));

          return updatedLead;
        })
        .catch(err => this.error(err))
        .finally(() => this.savingLeadIds = this.savingLeadIds.filter(id => id !== lead.id));
    },
    changeStatus(lead, status) {
      this.saveLead(lead, () => LeadgenFormLeadService.updateStatus(lead.id, status));
    },
    changeAssignee(lead, userId) {
      this.saveLead(lead, () => LeadgenFormLeadService.updateAssignee(lead.id, userId));
    },
    toggleColumn(key) {
      const keys = this.visibleColumnKeys.includes(key)
        ? this.visibleColumnKeys.filter(item => item !== key)
        : [ ...this.visibleColumnKeys, key ];

      const previousColumns = this.leadgenForm.columns;
      this.leadgenForm.columns = keys;

      LeadgenFormService.updateColumns(this.leadgenFormId, keys)
        .catch(err => {
          this.leadgenForm.columns = previousColumns;
          this.error(err);
        });
    },
    refresh() {
      this.isRefreshing = true;

      return LeadgenFormService.refreshLeads(this.leadgenFormId)
        .then(resp => {
          this.leadgenForm.updateWith({
            leads_count: resp.leads_count,
            leads_fetched_at: resp.leads_fetched_at,
          });
          return resp;
        })
        .then(resp => this.loadLeadgenForm().then(() => resp))
        .catch(err => this.error(err))
        .finally(() => this.isRefreshing = false);
    },
    download() {
      this.isDownloading = true;

      return LeadgenFormService.downloadLeadsCSV(this.leadgenFormId)
        .then(response => {
          const fallbackFilename = `leadgen_form_${this.leadgenFormId}_leads_${Date.now()}.csv`;

          let filename = fallbackFilename;
          const contentDisposition = response.headers?.['content-disposition'];
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, '');
            }
          }

          const href = URL.createObjectURL(response.data);

          const link = document.createElement('a');
          link.href = href;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          URL.revokeObjectURL(href);

          return response;
        })
        .catch(err => this.error(err))
        .finally(() => this.isDownloading = false);
    },
  },
};
</script>
